import _ from 'lodash'
import { Progress } from '@agro-club/agroclub-shared'
import { useAProgress } from 'hooks/useAProgress'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC, KeyboardEventHandler, useCallback, useEffect, useState } from 'react'
import { TableInput } from 'views/components/Table/Controls/TableInput'
import { ProductData, User } from 'modules/domain/user/types'
import { refetchFunc } from 'modules/domain/common/hooks'
import { useDebounceFunc } from 'hooks/useDebounceFunc'
import InlineProgress from './InlineProgress'

type Props = {
  data: ProductData
  fieldName: string
  refetch: refetchFunc
  user: User
  disabled: boolean
}
const EditableCell: FC<Props> = ({ data, fieldName, user, refetch, disabled }) => {
  const extVal = data[fieldName]
  const [currentVal, setCurrentValue] = useState(extVal)
  const [progress, execute, error] = useAProgress(
    async (value: string) => {
      if (!user.profile?.company) {
        return
      }
      if (data.id) {
        await apiClient.patch(endpoints.companyProductData(user.profile.company.id, data.id.toString()), {
          [fieldName]: value === '' ? null : value,
        })
      }
      await refetch(true)
    },
    {
      eventName: 'price',
      scope: 'change',
    },
  )

  const updateValue = useCallback(
    (val: string) => {
      if (val === currentVal) {
        return
      }
      return execute(val)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentVal],
  )

  useEffect(() => {
    setCurrentValue(extVal)
  }, [extVal])

  // Debounced update when user stops typing
  const debouncedUpdate = useDebounceFunc(updateValue, 500)

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key !== 'Enter' || progress === Progress.WORK) {
      return
    }
    updateValue(currentVal)
  }

  // Handle input changes to trigger debounce
  const handleInputChange = (e) => {
    setCurrentValue(e.target.value)
    debouncedUpdate(e.target.value)
  }

  return (
    <div style={{ position: 'relative' }}>
      <InlineProgress enabled={progress === Progress.WORK} />
      <TableInput
        error={!!error}
        onChange={handleInputChange}
        onBlur={() => updateValue(currentVal)}
        onKeyDown={onKeyDown}
        value={currentVal}
        visible
        disabled={disabled || !data.id}
      />
    </div>
  )
}

export default EditableCell
